import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarClock,
  LifeBuoy,
  Lightbulb,
  MessageSquare,
  Settings,
  UserRound,
  Users,
  Wand2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { getUnreadCounts } from "@/lib/api/ai-threads";
import { getUnreadCommentCount } from "@/lib/api/support-tickets";
import { useSidebar } from "@/lib/context/sidebar-context";
import { useAppBootstrap } from "@/lib/hooks/use-app-bootstrap";
import { useAutoContext } from "@/lib/hooks/use-auto-context";
import { useClient } from "@/lib/hooks/use-client-detail";
import { useT } from "@/lib/i18n/use-t";
import { AiSettingsPanel } from "./ai-settings/ai-settings-panel";
import { ChatPanel } from "./chat-panel";
import { DocumentationPanel } from "./documentation-panel";
import { IdeasPanel } from "./ideas-panel";
import { SchedulerPanel } from "./scheduler-panel";
import { T } from "./sidebar.i18n";
import { SupportPanel } from "./support-panel";

type SidebarProps = {
  chartColors?: string[] | null;
};

export function Sidebar({ chartColors }: SidebarProps) {
  const { activeTab, isOpen, width, setWidth, toggleTab } = useSidebar();
  const t = useT(T);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Auto-context for client chat
  const autoContext = useAutoContext();
  const { client: activeClientData } = useClient(autoContext.clientId);

  // Designer-only "System AI" tab. Same gate as the Settings nav link.
  const { data: bootstrap } = useAppBootstrap();
  const userPermissions = bootstrap?.user.permissions ?? [];

  // Unread counts for the AI Agent / System AI icon badges.
  // 15s poll while logged in — cheap (2-int response), no SSE plumbing needed.
  // QL-74 — the Client Agent tab has no badge: client chats are shared by the
  // whole team, so a team-wide unread count is noise.
  const { data: unread } = useQuery({
    queryKey: ["ai-threads", "unread-count"],
    queryFn: getUnreadCounts,
    refetchInterval: 15_000,
    enabled: !!bootstrap,
  });
  // Support badge: tickets with a comment this user has not read. Same 15s poll
  // and same shape as the AI thread counts above. Ideas deliberately has none —
  // it is a suggestion board, not a conversation anyone is waiting on.
  const { data: supportUnread } = useQuery({
    queryKey: ["support-tickets", "unread-count"],
    queryFn: getUnreadCommentCount,
    refetchInterval: 15_000,
    enabled: !!bootstrap,
  });

  const isDesigner = userPermissions.includes("designer");
  // Tenant-level kill switch — hides chat/client-chat/design-agent/scheduler tabs.
  // Defaults to true (and bootstrap may still be loading), so absence == show.
  const aiAgentsEnabled = bootstrap?.aiAgentsEnabled !== false;

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      const constrainedWidth = Math.max(300, Math.min(800, newWidth));
      setWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setWidth]);

  const tabs = [
    {
      id: "docs" as const,
      label: t.documentation,
      icon: <BookOpen className="h-5 w-5" />,
      badge: 0,
      panel: <DocumentationPanel isActive={activeTab === "docs" && isOpen} />,
    },
    ...(aiAgentsEnabled
      ? [
          {
            id: "chat" as const,
            label: t.aiAgent,
            icon: <MessageSquare className="h-5 w-5" />,
            badge: unread?.global ?? 0,
            panel: <ChatPanel chartColors={chartColors} mode="global" />,
          },
          {
            id: "client-chat" as const,
            label: autoContext.clientId ? activeClientData?.name || t.clientAgent : t.clientAgent,
            icon: <UserRound className="h-5 w-5" />,
            badge: 0,
            panel: autoContext.clientId ? (
              <ChatPanel
                chartColors={chartColors}
                mode="client"
                clientId={autoContext.clientId}
                clientName={activeClientData?.name}
                flowInstanceId={autoContext.flowInstanceId}
              />
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-6 h-16">
                  <h2 className="text-xl font-semibold truncate">{t.clientAgent}</h2>
                </div>
                <div className="flex items-center gap-1.5 px-6 pb-2 text-[11px] text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span>{t.visibilityShared}</span>
                </div>
                <Separator />
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                  <UserRound className="h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">{t.clientAgentEmpty}</p>
                </div>
              </div>
            ),
          },
          ...(isDesigner
            ? [
                {
                  id: "system-ai" as const,
                  label: t.systemAi,
                  icon: <Wand2 className="h-5 w-5" />,
                  badge: unread?.system ?? 0,
                  panel: <ChatPanel chartColors={chartColors} mode="system" />,
                },
              ]
            : []),
          {
            id: "scheduler" as const,
            label: t.scheduler,
            icon: <CalendarClock className="h-5 w-5" />,
            badge: 0,
            panel: <SchedulerPanel />,
          },
        ]
      : []),
    {
      id: "ideas" as const,
      label: t.ideas,
      icon: <Lightbulb className="h-5 w-5" />,
      badge: 0,
      panel: <IdeasPanel />,
    },
    {
      id: "support" as const,
      label: t.support,
      icon: <LifeBuoy className="h-5 w-5" />,
      badge: supportUnread?.count ?? 0,
      panel: <SupportPanel />,
    },
    {
      id: "ai-settings" as const,
      label: "AI Settings",
      icon: <Settings className="h-5 w-5" />,
      badge: 0,
      panel: (
        <AiSettingsPanel clientId={autoContext.clientId} clientName={activeClientData?.name} />
      ),
    },
  ];

  return (
    <>
      {/* Vertical Tab Bar */}
      <div
        className="fixed top-0 bottom-0 z-50 flex bg-background border-r transition-all duration-300 ease-in-out"
        style={{
          left: isOpen ? `${width}px` : "0",
          width: isOpen ? "48px" : "44px",
          transition: isResizing ? "none" : "all 300ms ease-in-out",
        }}
      >
        <div className="flex flex-col gap-2 py-4 flex-1 items-center">
          {tabs.map((tab) => (
            <div key={tab.id} className="relative">
              <Button
                variant={activeTab === tab.id ? "default" : "ghost"}
                size="icon"
                onClick={() => toggleTab(tab.id)}
                className="h-8 w-8"
                tooltip={tab.label}
                // The rail is vertical, so a top-side tooltip sits on the button
                // above it and scanning down the icons drops the pointer into the
                // tooltip rather than onto the next one.
                tooltipSide="right"
              >
                {tab.icon}
              </Button>
              {tab.badge > 0 && (
                <span
                  role="status"
                  aria-label={`${tab.badge} unread`}
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-semibold flex items-center justify-center pointer-events-none"
                >
                  {tab.badge > 99 ? "99+" : tab.badge}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Resize Handle */}
        {isOpen && (
          // biome-ignore lint/a11y/noStaticElementInteractions: resize handle requires mouse interaction
          <div
            className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 z-50 flex items-center justify-center"
            onMouseDown={() => setIsResizing(true)}
          >
            <div className="h-12 w-0.5 bg-muted-foreground/30 rounded-full" />
          </div>
        )}
      </div>

      {/* Sidebar Panel */}
      <div
        ref={sidebarRef}
        className="fixed top-0 bottom-0 bg-background border-r shadow-lg z-40 flex"
        style={{
          left: isOpen ? "0" : `-${width}px`,
          width: `${width}px`,
          transition: isResizing ? "none" : "all 300ms ease-in-out",
        }}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="flex-1 min-w-0 overflow-hidden"
            style={{
              display: activeTab === tab.id ? "flex" : "none",
              flexDirection: "column",
            }}
          >
            {tab.panel}
          </div>
        ))}
      </div>
    </>
  );
}
